import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useSetAtom } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { endImpersonation } from "@/api/admin";
import { useAuthStore } from "@/store/auth";
import {
  clearImpersonationContext,
  impersonationContextAtom,
  isImpersonatingAtom,
  readImpersonationContext,
} from "@/store/impersonation";

/**
 * Shared hook that ends an active impersonation session and returns the
 * superadmin to the admin panel. Centralizes the token-restore + cache-clear +
 * navigate flow so both the dispatcher AppShell banner and the admin/dealer
 * "you are impersonating" blocked state exit the same way (TASK-3677).
 *
 * Sprint 100 caution: impersonation `ended_at` is token-order sensitive — the
 * actor token is restored to `auth_token` BEFORE the `endImpersonation`
 * request so it carries the actor identity (the end endpoint runs under the
 * actor's role guard and would 403 under the impersonation token). Do not
 * reorder.
 *
 * Sprint 139: the exit DESTINATION is actor-dependent (Decision D5) — a
 * superadmin→(tenant|dealer) session returns to `/admin/dashboard`; a
 * dealer→tenant session returns to `/dealer/dashboard`. It is read from the
 * persisted impersonation context, falling back to `/admin/dashboard` for
 * legacy sessions started before the context existed.
 */
export function useExitImpersonation(): () => Promise<void> {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initFromStorage = useAuthStore((s) => s.initFromStorage);
  const setIsImpersonating = useSetAtom(isImpersonatingAtom);
  const setImpersonationContext = useSetAtom(impersonationContextAtom);

  return useCallback(async () => {
    // Resolve the exit destination from the stored context BEFORE clearing it.
    const context = readImpersonationContext();
    const exitPath = context?.exitPath ?? "/admin/dashboard";

    // Restore the actor token FIRST so the end-session request below carries
    // the actor token. initFromStorage handles missing/empty tokens gracefully.
    localStorage.setItem(
      "auth_token",
      localStorage.getItem("superadmin_access_token") ?? "",
    );
    localStorage.setItem(
      "refresh_token",
      localStorage.getItem("superadmin_refresh_token") ?? "",
    );
    localStorage.removeItem("superadmin_access_token");
    localStorage.removeItem("superadmin_refresh_token");
    clearImpersonationContext();
    setImpersonationContext(null);
    setIsImpersonating(false);
    // Re-hydrate the auth store then drop all cached queries so the actor never
    // sees impersonated data after returning to their panel (BUG-186).
    await initFromStorage();
    // Best-effort: record the session end now that the actor token is active.
    // Never block exit on failure.
    try {
      await endImpersonation();
    } catch {
      // Ignore — exiting impersonation must always succeed locally.
    }
    queryClient.clear();
    void navigate(exitPath);
  }, [
    navigate,
    setIsImpersonating,
    setImpersonationContext,
    initFromStorage,
    queryClient,
  ]);
}
